import React, { useEffect } from 'react';

import { Button, Group, Modal, Stack, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { UseMutationResult } from 'react-query';
import { ErrorMessage, PayrollPeriod, PayrollPeriodCreate } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface IModalPayrollPeriodCreateProps {
  opened: boolean;
  closeHandler: () => void;
  handlePayrollPeriodChange: (value: string | null) => void;
  createMutation: UseMutationResult<
    AxiosResponse<PayrollPeriod>,
    AxiosError<ErrorMessage>,
    PayrollPeriodCreate
  >;
}

interface FormValues {
  startDate: Date | null;
  endDate: Date | null;
}

const ModalPayrollPeriodCreate = ({
  opened,
  handlePayrollPeriodChange,
  createMutation,
  closeHandler,
}: IModalPayrollPeriodCreateProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      startDate: null,
      endDate: null,
    },
    validate: values => ({
      startDate: values.startDate === null ? 'A start date is required' : null,
      endDate: values.endDate === null ? 'An end date is required' : null,
    }),
  });

  const handleCloseForm = (): void => {
    form.reset();
    closeHandler();
  };

  const handleSubmit = (formValues: FormValues): void => {
    const payload = {
      ...formValues,
      startDate: dayjs(formValues.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(formValues.endDate).format('YYYY-MM-DD'),
      payrollPeriodId: uuidv4(),
    };
    createMutation.mutate(payload, {
      onError: error => {
        errorNotification('Payroll period create failed', error.response?.data.message);
      },
      onSuccess: res => {
        successNotification('Payroll period create success.');
        handlePayrollPeriodChange(res.data.payrollPeriodId);
        handleCloseForm();
      },
    });
  };

  useEffect(() => {
    if (form.values.startDate && form.values.endDate && form.values.startDate > form.values.endDate) {
      form.setFieldValue('endDate', dayjs(form.values.startDate).add(1, 'd').toDate());
    }
  }, [form]);

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseForm}
      size='lg'
      title={
        <Title order={4} pr='md'>
          Create Payroll Period
        </Title>
      }
      withinPortal
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack p='md'>
          <Group grow noWrap>
            <DatePickerInput
              {...form.getInputProps('startDate')}
              data-cy='payrollPeriodStartDate'
              label='Begin Date'
              clearable
              popoverProps={{ zIndex: 10000000, withinPortal: true }}
              {...{ placeholder: 'Select date' }}
            />
            <DatePickerInput
              {...form.getInputProps('endDate')}
              data-cy='payrollPeriodEndDate'
              label='End Date'
              clearable
              popoverProps={{ zIndex: 10000000, withinPortal: true }}
              minDate={dayjs(form.values.startDate).toDate()}
              {...{ placeholder: 'Select date' }}
            />
          </Group>
        </Stack>
        <Group position='right' pt='md'>
          <Button
            variant='light'
            onClick={handleCloseForm}
            size='xs'
            radius='md'
            disabled={createMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            size='xs'
            radius='md'
            type='submit'
            loading={createMutation.isLoading}
            data-cy='createPayrollPeriodButtonSubmit'
          >
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default ModalPayrollPeriodCreate;
